/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { pushToDataLayer } from "utils/gtm";
import { Button, FontAwesomeIcon, Heading, Stack } from "components";
import { useTranslation } from "react-i18next";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import useTryCatch from "hooks/useTryCatch";
import { completeGuest } from "lib/userApi";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useUI } from "state";
import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import { useUser } from "state/UserContext";
import { ScreenPage } from "templates";
import PageNote from "templates/PageNote";
import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import useLocalStorage from "utils/useLocalStorage";
import { useOrgData } from "state/OrgDataContext";
import useRouting from "hooks/useRouting";
import { useEffect } from "react";

const DonePage = () => {
  const { t } = useTranslation();
  const { event: EVENT } = useEnvs();
  const [id] = useLocalStorage(LOCAL_STORAGE_KEYS.id, null);
  const { setLoading, isLoading, userData, setFormCompleted, setUserData } =
    useUser();
  const { colorScheme } = useUI();
  const EVENT_ASSETS = useAppAssetsWithSrc();
  const { dataContent: EVENT_CONTENT } = useOrgData();
  const { navigateWithEvent, eventId } = useEventRouting();
  const { shouldEnterDonePage } = useRouting();
  useEffect(() => {
    async function checkIfShouldEnterDonePage() {
      const goTo = await shouldEnterDonePage();
      if (goTo && goTo !== ROUTES.onboard.done) {
        navigateWithEvent(goTo);
      }
    }
    checkIfShouldEnterDonePage();
  }, []);
  const handleOnSubmit = useTryCatch(async (e) => {
    setLoading(true);
    pushToDataLayer({
      event: "go_to_dashboard",
      screen_name: "done",
      journey_step: "post onboarding",
    });
    const { ok } = await completeGuest(id, eventId);

    setLoading(false);
    if (ok) {
      setFormCompleted(true);
      const updatedUserData = { ...userData, is_form_completed: true };
      setUserData(updatedUserData);
      navigateWithEvent(ROUTES.dashboard.base);
    }
  }, setLoading);
  return (
    <ScreenPage
      desktopBG={
        colorScheme === "light"
          ? EVENT_ASSETS.alldone_BG_desktop.src
          : EVENT_ASSETS.alldone_BG_desktopDark.src
      }
      desktopBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.alldone_BG_desktop.fallbackSrc
          : EVENT_ASSETS.alldone_BG_desktopDark.fallbackSrc
      }
      mobileBG={
        colorScheme === "light"
          ? EVENT_ASSETS.alldone_BG_mobile.src
          : EVENT_ASSETS.alldone_BG_mobileDark.src
      }
      mobileBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.alldone_BG_mobile.fallbackSrc
          : EVENT_ASSETS.alldone_BG_mobileDark.fallbackSrc
      }
    >
      <Stack component="article" className="h-full w-full">
        <div className="flex items-center gap-x-3">
          <Heading
            as="h2"
            className="text-2xl md:text-4xl -text--theme-colors-text-title"
          >
            {t("done.title")}
          </Heading>{" "}
          <div className="-text--theme-colors-text-title">
            <FontAwesomeIcon name="fa-circle-check" size="xl" />
          </div>
        </div>
        <PageNote
          note={
            EVENT === "SPORTICO"
              ? EVENT_CONTENT.done?.description
              : t("done.description", { returnObjects: true })
          }
          hasPolicy={false}
          spacing={5}
          className="-text--theme-colors-text-body text-sm md:text-base mt-2.5 md:mt-4"
        />

        <div className="mt-10 md:mt-[50px]">
          <Button
            variant="primary"
            size={"lg"}
            onClick={handleOnSubmit}
            isLoading={isLoading}
            fullWidth
          >
            {t("done.continueButtonLabel")}
          </Button>
        </div>
      </Stack>
    </ScreenPage>
  );
};
export default DonePage;
